import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { affiliatePortalApi, affiliateAuth } from "../lib/affiliateApi";
import AffiliateContractGateModal from "../components/AffiliateContractGateModal";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";
const REF_CODE_RE = /^[a-z0-9-]+$/;

const TYPE_STYLE = {
    first:     { bg: "bg-amber-500/15 text-amber-200 border-amber-500/30", label: "1º" },
    second:    { bg: "bg-orange-500/15 text-orange-200 border-orange-500/30", label: "2º" },
    recurring: { bg: "bg-sky-500/15 text-sky-200 border-sky-500/30", label: "Rec." },
};
const STATUS_STYLE = {
    active:  { bg: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", label: "Ativo" },
    trial:   { bg: "bg-amber-500/15 text-amber-200 border-amber-500/30", label: "Trial" },
    pending: { bg: "bg-amber-500/15 text-amber-200 border-amber-500/30", label: "Pendente" },
    paid:    { bg: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", label: "Pago" },
};
const Badge = ({ style }) => (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${style?.bg || ""}`}>{style?.label || "—"}</span>
);

// ─── Login ──────────────────────────────────────────────────────────────
const AffiliateLogin = ({ onSuccess }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const r = await affiliatePortalApi.login(email.trim().toLowerCase(), password);
            toast.success(`Olá, ${r.affiliate.name.split(" ")[0]}!`);
            onSuccess?.();
        } catch (err) { toast.error("Não foi possível entrar", { description: err.message }); }
        finally { setLoading(false); }
    };

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200 flex items-center justify-center px-6" data-testid="affiliate-login-page">
            <form onSubmit={submit} className="max-w-md w-full space-y-4">
                <div className="text-center mb-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Portal do afiliado</div>
                    <h1 className="salon-heading text-3xl text-white">Kinng.app</h1>
                    <p className="text-xs text-slate-500 mt-1">Acompanhe suas indicações e comissões.</p>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">E-mail</div>
                    <input data-testid="affiliate-input-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="voce@email.com"
                        className="w-full bg-slate-900/60 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-amber-500/40" />
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Senha</div>
                    <div className="relative">
                        <input data-testid="affiliate-input-password" type={showPassword ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                            placeholder="sua senha"
                            className="w-full bg-slate-900/60 border border-white/10 rounded-xl pl-4 pr-11 py-3 text-sm outline-none focus:border-amber-500/40" />
                        <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                            className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-slate-500 hover:text-amber-200"
                            data-testid="affiliate-password-toggle"
                        >
                            <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"} text-sm`}></i>
                        </button>
                    </div>
                </div>
                <button data-testid="affiliate-login-btn" type="submit" disabled={loading}
                    className="w-full rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-3 text-sm disabled:opacity-40 transition-all">
                    {loading ? "Entrando…" : "Entrar no portal"}
                </button>
                <div className="text-center text-xs text-slate-500 pt-2">
                    Quer ser afiliado?{" "}
                    <Link to="/afiliado/cadastro" className="text-amber-300 hover:underline" data-testid="affiliate-link-signup">
                        Cadastre-se aqui
                    </Link>
                </div>
            </form>
        </div>
    );
};

// ─── Dashboard ──────────────────────────────────────────────────────────
const AffiliateDashboard = ({ onLogout }) => {
    const [data, setData] = useState(null);
    const [referrals, setReferrals] = useState([]);
    const [commissions, setCommissions] = useState([]);
    const [downloading, setDownloading] = useState(false);
    const [editingCode, setEditingCode] = useState(false);
    const [codeInput, setCodeInput] = useState("");
    const [savingCode, setSavingCode] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [d, r, c] = await Promise.all([
                    affiliatePortalApi.dashboard(),
                    affiliatePortalApi.referrals(),
                    affiliatePortalApi.commissions(),
                ]);
                setData(d); setReferrals(r || []); setCommissions(c || []);
            } catch (e) {
                toast.error("Erro ao carregar", { description: e.message });
                if (String(e.message).includes("401") || String(e.message).includes("403")) {
                    onLogout?.();
                }
            }
        })();
    }, [onLogout]);

    const downloadPdf = async () => {
        setDownloading(true);
        try {
            const blob = await affiliatePortalApi.pdfBlob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `kinng-comissoes-${data.affiliate.ref_code}-${new Date().toISOString().slice(0,7)}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Falha ao gerar PDF", { description: e.message }); }
        finally { setDownloading(false); }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(data.ref_link);
        toast.success("Link copiado!");
    };

    const startEditCode = () => {
        setCodeInput(data.affiliate.ref_code);
        setEditingCode(true);
    };

    const saveCode = async () => {
        const code = codeInput.trim().toLowerCase();
        if (code.length < 3 || code.length > 30 || !REF_CODE_RE.test(code)) {
            toast.error("Código inválido", { description: "Use apenas letras minúsculas, números e hífens (3 a 30 caracteres)." });
            return;
        }
        setSavingCode(true);
        try {
            const res = await affiliatePortalApi.updateRefCode(code);
            setData((d) => ({ ...d, affiliate: res.affiliate, ref_link: res.ref_link }));
            setEditingCode(false);
            toast.success("Código atualizado!");
        } catch (e) {
            toast.error(e.message || "Falha ao salvar código");
        } finally {
            setSavingCode(false);
        }
    };

    const maxMonth = useMemo(() => Math.max(1, ...(data?.monthly || []).map((m) => m.total)), [data]);

    if (!data) {
        return <div className="min-h-screen bg-slate-950 text-slate-500 flex items-center justify-center">Carregando…</div>;
    }

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200" data-testid="affiliate-portal-page">
            <header className="border-b border-white/5">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Portal do afiliado</div>
                        <h1 className="salon-heading text-xl text-white">Olá, {data.affiliate.name.split(" ")[0]}!</h1>
                    </div>
                    <div className="flex gap-2">
                        <button data-testid="affiliate-pdf-btn" onClick={downloadPdf} disabled={downloading}
                            className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20 px-4 py-2 text-xs disabled:opacity-40">
                            <i className="fa-solid fa-file-pdf mr-2"></i>{downloading ? "Gerando…" : "Baixar PDF"}
                        </button>
                        <button data-testid="affiliate-logout-btn" onClick={onLogout}
                            className="rounded-xl border border-white/10 text-slate-400 hover:text-white px-3 py-2 text-xs">
                            <i className="fa-solid fa-right-from-bracket"></i>
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">
                {/* Ref link */}
                <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5" data-testid="affiliate-ref-card">
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Seu link de indicação</div>
                        {!editingCode && (
                            <button
                                type="button"
                                data-testid="affiliate-edit-code-btn"
                                onClick={startEditCode}
                                className="text-[10px] uppercase tracking-wider text-amber-300/80 hover:text-amber-200 transition-colors"
                            >
                                <i className="fa-solid fa-pen mr-1"></i>Editar código
                            </button>
                        )}
                    </div>

                    {editingCode ? (
                        <div className="space-y-2">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                                <div className="flex-1 w-full flex items-center rounded-xl bg-slate-950/60 border border-white/10 focus-within:border-amber-500/50 px-4 py-3">
                                    <span className="text-slate-500 text-sm font-mono shrink-0">kinng.app/register?ref=</span>
                                    <input
                                        data-testid="affiliate-code-input"
                                        value={codeInput}
                                        onChange={(e) => setCodeInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                                        maxLength={30}
                                        autoFocus
                                        className="flex-1 min-w-0 bg-transparent text-sm font-mono text-amber-100 outline-none"
                                    />
                                </div>
                                <div className="flex gap-2 w-full sm:w-auto">
                                    <button
                                        data-testid="affiliate-save-code-btn"
                                        onClick={saveCode}
                                        disabled={savingCode}
                                        className="flex-1 sm:flex-none rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-5 py-3 text-sm disabled:opacity-50"
                                    >
                                        {savingCode ? "Salvando…" : "Salvar"}
                                    </button>
                                    <button
                                        data-testid="affiliate-cancel-code-btn"
                                        onClick={() => setEditingCode(false)}
                                        disabled={savingCode}
                                        className="flex-1 sm:flex-none rounded-xl border border-white/10 text-slate-300 hover:text-white px-4 py-3 text-sm disabled:opacity-50"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                            <p className="text-[11px] text-slate-500">Apenas letras minúsculas, números e hífens · 3 a 30 caracteres.</p>
                        </div>
                    ) : (
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                            <div className="flex-1 rounded-xl bg-slate-950/60 border border-white/10 px-4 py-3 font-mono text-sm text-amber-100 truncate">
                                {data.ref_link}
                            </div>
                            <button data-testid="affiliate-copy-link" onClick={copyLink}
                                className="rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-5 py-3 text-sm">
                                <i className="fa-solid fa-copy mr-2"></i>Copiar
                            </button>
                        </div>
                    )}

                    <div className="mt-3 text-xs" data-testid="affiliate-link-expiry">
                        {data.affiliate.expires_at ? (
                            <span className={data.affiliate.link_expired ? "text-red-400" : "text-slate-500"}>
                                <i className={`fa-solid ${data.affiliate.link_expired ? "fa-triangle-exclamation" : "fa-clock"} mr-1.5`}></i>
                                {data.affiliate.link_expired
                                    ? `Link expirado em ${fmtDate(data.affiliate.expires_at)}`
                                    : `Link válido até ${fmtDate(data.affiliate.expires_at)}`}
                            </span>
                        ) : (
                            <span className="text-slate-500">
                                <i className="fa-solid fa-infinity mr-1.5"></i>Link sem prazo de validade
                            </span>
                        )}
                    </div>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {[
                        ["Indicações", data.kpis.total_referrals, "fa-users", "text-white"],
                        ["Ativas", data.kpis.active_tenants, "fa-user-check", "text-emerald-300"],
                        ["Comissão total", brl(data.kpis.commission_total), "fa-sack-dollar", "text-amber-300"],
                        ["A receber", brl(data.kpis.commission_pending), "fa-hourglass", "text-amber-300"],
                        ["Já recebido", brl(data.kpis.commission_paid), "fa-check", "text-emerald-300"],
                    ].map(([l, v, i, cls]) => (
                        <div key={l} className="rounded-xl border border-white/5 bg-slate-900/40 p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1"><i className={`fa-solid ${i}`}></i>{l}</div>
                            <div className={`salon-heading text-xl mt-1 ${cls}`}>{v}</div>
                        </div>
                    ))}
                </div>

                {/* Chart */}
                <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 mb-3">Comissões recebidas (últimos 6 meses)</div>
                    {data.monthly.length === 0 ? (
                        <div className="text-xs text-slate-500 text-center py-6">Ainda não há comissões pagas.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.monthly.map((m) => (
                                <div key={m.month} className="flex items-center gap-2 text-xs">
                                    <div className="w-16 text-slate-500 font-mono">{m.month}</div>
                                    <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
                                        <div className="h-full bg-sky-400" style={{ width: `${(m.total / maxMonth) * 100}%` }}></div>
                                    </div>
                                    <div className="w-24 text-right text-sky-300 font-mono">{brl(m.total)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Tables */}
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Indicações ({referrals.length})</div>
                        {referrals.length === 0 ? (
                            <div className="text-xs text-slate-500 text-center py-4">Compartilhe seu link para começar!</div>
                        ) : (
                            <div className="space-y-2">
                                {referrals.map((r) => (
                                    <div key={r.id} className="rounded-lg border border-white/5 bg-slate-950/40 p-3" data-testid={`affiliate-referral-${r.id}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <div className="text-sm text-white truncate">{r.shop_name}</div>
                                            <Badge style={STATUS_STYLE[r.status]} />
                                        </div>
                                        <div className="flex items-center justify-between text-[11px] text-slate-500">
                                            <span>{fmtDate(r.created_at)} · {r.plan}</span>
                                            <span className="text-emerald-300 font-mono">{brl(r.commission_generated)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-4">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Comissões ({commissions.length})</div>
                        {commissions.length === 0 ? (
                            <div className="text-xs text-slate-500 text-center py-4">Nenhuma comissão ainda.</div>
                        ) : (
                            <div className="space-y-2">
                                {commissions.map((c) => (
                                    <div key={c.id} className="rounded-lg border border-white/5 bg-slate-950/40 p-3" data-testid={`affiliate-commission-${c.id}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <Badge style={TYPE_STYLE[c.type]} />
                                            <Badge style={STATUS_STYLE[c.status]} />
                                        </div>
                                        <div className="flex items-center justify-between text-xs">
                                            <div className="text-slate-400 truncate">
                                                {c.tenant_name}
                                                {c.paid_at && <span className="text-slate-500"> · pago {fmtDate(c.paid_at)}</span>}
                                            </div>
                                            <div className="font-mono text-emerald-300 shrink-0">{brl(c.amount)}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
};

// ─── Root ───────────────────────────────────────────────────────────────
const AffiliatePortal = () => {
    const [loggedIn, setLoggedIn] = useState(() => !!affiliateAuth.getToken());
    const navigate = useNavigate();

    useEffect(() => {
        // Verifica token válido
        if (affiliateAuth.getToken()) {
            affiliatePortalApi.me().catch(() => {
                affiliateAuth.clear();
                setLoggedIn(false);
            });
        }
    }, []);

    const logout = () => {
        affiliateAuth.clear();
        setLoggedIn(false);
    };

    if (!loggedIn) return <AffiliateLogin onSuccess={() => setLoggedIn(true)} />;
    return (
        <>
            <AffiliateDashboard onLogout={logout} />
            {/* Gate de reaceite do contrato — roda a cada login, sobrepõe as
                métricas até o afiliado aceitar a versão vigente. */}
            <AffiliateContractGateModal />
        </>
    );
};

export default AffiliatePortal;
